import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import analytics from '../utils/analytics'
import { useSupabaseAuth } from '../hooks/useSupabaseAuth.js'

export default function MobileNavigation() {
  const { user, isSignedIn, signOut, isAdmin } = useSupabaseAuth()
  const location = useLocation()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [deferredPrompt, setDeferredPrompt] = useState(null)
  const [isInstallable, setIsInstallable] = useState(false)
  const [isInstalled, setIsInstalled] = useState(false)
  const menuRef = useRef(null)
  const hamburgerRef = useRef(null)
  const navigate = useNavigate()

  useEffect(() => {
    setupScrollListener()
    setupInstallListener()
  }, [user])

  useEffect(() => {
    // Close menu when route changes
    setIsMenuOpen(false)
  }, [location])

  const setupInstallListener = () => {
    // Check if app is already installed
    const checkIfInstalled = () => {
      if (window.matchMedia('(display-mode: standalone)').matches) {
        setIsInstalled(true)
        setIsInstallable(false)
        return
      }
      
      if (window.navigator.standalone === true) {
        setIsInstalled(true)
        setIsInstallable(false)
        return
      }
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault()
      setDeferredPrompt(e)
      setIsInstallable(true)
    }

    // Listen for appinstalled event
    const handleAppInstalled = () => {
      setIsInstalled(true)
      setIsInstallable(false)
      setDeferredPrompt(null)
    }

    checkIfInstalled()
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      if (navigator.userAgent.includes('Chrome')) {
        alert('To install the Park Model Prices app:\n\n1. Click the menu (⋮) in your browser\n2. Select "Install Park Model Prices"\n3. Click "Install"')
      } else {
        alert('To install the Park Model Prices app, look for the install option in your browser menu.')
      }
      return
    }

    try {
      deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice
      
      if (outcome === 'accepted') {
        setIsInstalled(true)
        setIsInstallable(false)
      }
      
      setDeferredPrompt(null)
    } catch (error) {
      console.error('Error during installation:', error)
    }
  }

  const setupScrollListener = () => {
    const handleScroll = () => {
      // Increased threshold to 20px for better consistency across mobile browsers
      setIsScrolled(window.scrollY > 20)
    }
    
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }

  const toggleMenu = () => {
    const newState = !isMenuOpen
    setIsMenuOpen(newState)
    
    // Prevent body scroll when menu is open
    document.body.style.overflow = newState ? 'hidden' : ''
    
    // Track menu interaction
    analytics.trackEvent('mobile_menu_toggle', {
      action: newState ? 'open' : 'close',
      location: location.pathname
    })
  }

  const closeMenu = () => {
    setIsMenuOpen(false)
    document.body.style.overflow = ''
  }

  const goTo = (path, label) => {
    analytics.trackEvent('mobile_menu_navigation', {
      link: label,
      from: location.pathname
    })
    navigate(path)
    closeMenu()
  }

  return (
    <>
      {/* Mobile Header - Fixed with proper dark background by default */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-gray-900/95 backdrop-blur-md shadow-lg border-b border-gray-800' 
          : 'bg-gray-900/60 backdrop-blur-sm border-b border-gray-800'
      } safe-area-top`}>
        <div className="flex items-center justify-between px-4 py-3">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center space-x-2"
            onClick={() => handleMenuLinkClick('logo')}
          >
            <img 
              src="/logo/firefly-logo.png" 
              alt="Park Model Prices by Firefly Tiny Homes"
              className="h-8 w-auto"
            />
          </Link>

          {/* Try the App Link - Center with consistent underline */}
          {!isInstalled && (isInstallable || navigator.userAgent.includes('Chrome')) && (
            <button
              onClick={handleInstallClick}
              className="text-sm font-medium underline text-gray-100 hover:text-yellow-400"
            >
              Try the app!
            </button>
          )}

          {/* Hamburger Menu */}
          <button
            ref={hamburgerRef}
            data-mobile-menu
            onClick={toggleMenu}
            className="mobile-button hamburger p-2 text-gray-100"
            aria-label="Toggle mobile menu"
            aria-expanded={isMenuOpen}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div 
        className={`mobile-menu ${isMenuOpen ? 'active' : ''}`}
        onClick={closeMenu}
      >
        <div 
          ref={menuRef}
          data-mobile-menu-content
          data-swipe="left"
          className="mobile-menu-content safe-area-top safe-area-bottom"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Menu Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <img 
                src="/logo/firefly-logo.png" 
                alt="Park Model Prices by Firefly Tiny Homes"
                className="h-8 w-auto"
              />
            </div>
            <button
              onClick={closeMenu}
              className="mobile-button p-2"
              aria-label="Close menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Menu Content */}
          <nav className="flex-1 overflow-y-auto">
            <div className="p-4 space-y-2">
              {/* Main Navigation */}
              <div className="space-y-1">
                <button
                  type="button"
                  className="mobile-menu-link"
                  onClick={() => goTo('/', 'home')}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  <span>Home</span>
                </button>

                <button
                  type="button"
                  className="mobile-menu-link"
                  onClick={() => goTo('/models', 'models')}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span>Explore Models</span>
                </button>
              </div>

              {/* User Section */}
              <div className="pt-4 border-t border-gray-200">
                {isSignedIn ? (
                  <div className="space-y-1">
                    <button
                      type="button"
                      className="mobile-menu-link"
                      onClick={() => goTo('/builds', 'my-home')}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v6H8V5z" />
                      </svg>
                      <span>My Home</span>
                    </button>

                    <button
                      type="button"
                      className="mobile-menu-link"
                      onClick={() => goTo('/portal', 'orders')}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                      </svg>
                      <span>My Orders</span>
                    </button>

                    {isAdmin && (
                      <button
                        type="button"
                        className="mobile-menu-link"
                        onClick={() => goTo('/admin', 'admin')}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        <span>Admin</span>
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-1">
                    <button
                      type="button"
                      className="mobile-menu-link"
                      onClick={() => goTo('/sign-in', 'sign-in')}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                      </svg>
                      <span>Sign In</span>
                    </button>

                    <button
                      type="button"
                      className="mobile-menu-link"
                      onClick={() => goTo('/sign-up', 'sign-up')}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                      </svg>
                      <span>Create Account</span>
                    </button>
                  </div>
                )}
              </div>

              {/* About Section */}
              <div className="pt-4 border-t border-gray-200">
                <div className="space-y-1">
                  <button
                    type="button"
                    className="mobile-menu-link"
                    onClick={() => goTo('/faq', 'faq')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>FAQ</span>
                  </button>

                  <button
                    type="button"
                    className="mobile-menu-link"
                    onClick={() => goTo('/about', 'about')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>About Us</span>
                  </button>

                  <button
                    type="button"
                    className="mobile-menu-link"
                    onClick={() => goTo('/about/team', 'team')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <span>Our Team</span>
                  </button>

                  <button
                    type="button"
                    className="mobile-menu-link"
                    onClick={() => goTo('/about/manufacturer', 'manufacturer')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    <span>About the Manufacturer</span>
                  </button>
                </div>
              </div>

              {/* Contact Section */}
              <div className="pt-4 border-t border-gray-200">
                <div className="space-y-1">
                  <a
                    href="tel:+18302412410"
                    className="mobile-menu-link"
                    title="Call Firefly Tiny Homes"
                    onClick={() => handleMenuLinkClick('phone')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span>Call Us</span>
                  </a>

                  <a
                    href="mailto:office@fireflytinyhomes.com"
                    className="mobile-menu-link"
                    title="Email Firefly Tiny Homes"
                    onClick={() => handleMenuLinkClick('email')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span>Email Us</span>
                  </a>
                </div>
              </div>
            </div>
          </nav>

          {/* Sign Out at Bottom */}
          {isSignedIn && (
            <div className="p-4 border-t border-gray-200">
              <button
                onClick={() => {
                  handleMenuLinkClick('sign-out')
                  signOut()
                }}
                className="mobile-menu-link text-red-600 w-full"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>Sign Out</span>
              </button>
            </div>
          )}

          {/* Menu Footer */}
          <div className="p-4 border-t border-gray-200">
            <div className="text-sm text-gray-500 text-center">
              <p>(c) 2024 Firefly Tiny Homes</p>
              <p className="mt-1">Champion Park Model Homes</p>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
